/* eslint-disable max-lines-per-function, max-lines */
import { inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';

import { UsersDataService } from '@core/account-data';
import { TimeService } from '@core/common-legacy';
import { DisqualifyReasonsSelectors } from '@core/disqualify-reasons-data';
import { I18nService } from '@core/i18n-legacy';
import { JobsWrapperDataService } from '@core/offers-data';
import { jobStatusTranslationKeys } from '@core/offers-types';
import { TalentPoolsDataService } from '@core/talent-pools-data';
import {
  AutomationAction,
  AutomationActionType,
  AutomationConditionType,
  AutomationDescriptionCondition,
  AutomationTrigger,
  AutomationTriggerType,
} from '@core/workflow-automations-types';
import {
  AutomationDictionaryService,
  TriggerDescriptions,
} from '@recruitee/workflow-automations-util';

@Injectable({ providedIn: 'root' })
export class AutomationDescriptionHelper {
  private readonly i18n = inject(I18nService);
  private readonly store = inject(Store);
  private readonly automationDictionaryService = inject(AutomationDictionaryService);
  private readonly usersDataService = inject(UsersDataService);
  private readonly talentPoolsDataService = inject(TalentPoolsDataService);
  private readonly jobsWrapperDataService = inject(JobsWrapperDataService);
  private readonly timeService = inject(TimeService);

  private briefJobs = this.jobsWrapperDataService.getJobItemsIdMap();
  private talentPools = this.talentPoolsDataService.collectionMapSignal();
  private disqualifyReasons = this.store.selectSignal(
    DisqualifyReasonsSelectors.entitiesWithReferences(),
  );

  private translations = {
    hiringManager: this.i18n
      .translate(
        'app.automated_actions.action_editor.additional_user_selection_options.hiring_manager.heading',
      )
      .toLowerCase(),
    recruiter: this.i18n
      .translate(
        'app.automated_actions.action_editor.additional_user_selection_options.recruiter.heading',
      )
      .toLowerCase(),
  };

  public getTriggerDescription(trigger: Pick<AutomationTrigger, 'type' | 'data'>): string {
    if (!this.automationDictionaryService.triggerDescriptionsMap?.[trigger.type]) {
      console.error(`Trigger type ${trigger.type} does not have a description mapping.`);
      return '';
    }
    switch (trigger.type) {
      case AutomationTriggerType.CandidateTagAdded:
      case AutomationTriggerType.JobTagAdded: {
        const tagsTriggerData = trigger.data?.[0];
        return this.automationDictionaryService.triggerDescriptionsMap[trigger.type](
          <string[]>tagsTriggerData?.label || tagsTriggerData?.value,
        );
      }
      case AutomationTriggerType.JobDateHasPassed: {
        const date = trigger.data?.[0]?.value;
        return this.automationDictionaryService.triggerDescriptionsMap[trigger.type](
          (date &&
            this.timeService.format({
              input: date,
              pattern: 'DD MMM YYYY',
            })) ||
            '',
        );
      }
      case AutomationTriggerType.CandidateStartDateIs: {
        const defaultData = {
          timeRange: null,
          amount: null,
          unit: null,
        };

        const data = trigger.data?.reduce(
          (acc, item) => {
            if (item.name === 'time_range') {
              acc.timeRange = <string>item?.value || null;
            } else if (item.name === 'amount') {
              acc.amount = <string>item?.value ?? null;
            } else if (item.name === 'unit') {
              acc.unit = <string>item?.value || null;
            }
            return acc;
          },
          <Parameters<TriggerDescriptions[AutomationTriggerType.CandidateStartDateIs]>[0]>(
            defaultData
          ),
        );
        return this.automationDictionaryService.triggerDescriptionsMap[trigger.type](
          data || defaultData,
        );
      }
      default: {
        return this.automationDictionaryService.triggerDescriptionsMap[trigger.type](
          <string>trigger.data?.[0]?.label || '',
        );
      }
    }
  }

  public getConditionsDescription(
    conditions: AutomationDescriptionCondition[],
    useOnlyIf = false,
  ): string {
    if (!conditions?.length) {
      return '';
    }
    const onlyIf = `${!useOnlyIf ? this.automationDictionaryService.conditionTranslations.andOnlyIf : this.automationDictionaryService.conditionTranslations.onlyIf}`;
    const andIf = `${this.automationDictionaryService.conditionTranslations.and}`;
    const conditionsTexts = conditions.map(condition => {
      if (condition.type === null) return '';

      switch (condition.type) {
        case AutomationConditionType.CandidateEventOrganizer:
        case AutomationConditionType.CandidatePreferredLocation:
        case AutomationConditionType.CandidateSourceTags:
        case AutomationConditionType.CandidateOrigin:
        case AutomationConditionType.CandidateMovedFromStage:
        case AutomationConditionType.CandidateEvaluationScore:
        case AutomationConditionType.CandidateStage:
        case AutomationConditionType.JobDepartment:
        case AutomationConditionType.JobRecruiter:
        case AutomationConditionType.JobHiringManager:
        case AutomationConditionType.JobCreator:
        case AutomationConditionType.JobPreviousStatus: {
          const label = condition.data[0].selectedOption?.label;
          return (
            this.automationDictionaryService.conditionDescriptionsMap[condition.type]?.(
              label || '',
            ) || ''
          );
        }
        case AutomationConditionType.CandidateStartDate:
        case AutomationConditionType.CandidateTaskDueDate:
        case AutomationConditionType.CandidateEventDate: {
          const timeFrame =
            condition.data.find(d => d.name === 'timeframe')?.selectedOption?.label || null;
          const timeFrameValue =
            condition.data.find(d => d.name === 'timeframe')?.selectedOption?.value || null;
          const within =
            condition.data.find(d => d.name === 'within')?.selectedOption?.label || null;
          const period =
            condition.data.find(d => d.name === 'period')?.selectedOption?.label || null;
          const dateFrom =
            condition.data.find(d => d.name === 'date_from')?.selectedOption?.label || null;
          const dateTo =
            condition.data.find(d => d.name === 'date_to')?.selectedOption?.label || null;
          const date = condition.data.find(d => d.name === 'date')?.selectedOption?.label || null;
          return (
            this.automationDictionaryService.conditionDescriptionsMap[condition.type]?.({
              timeFrame,
              timeFrameValue: <string | null>timeFrameValue,
              within,
              period,
              dateFrom,
              dateTo,
              date,
            }) || ''
          );
        }
        case AutomationConditionType.JobWorkModel:
        case AutomationConditionType.CandidateTags:
        case AutomationConditionType.CandidateEventInterviewersInclude:
        case AutomationConditionType.CandidateTaskAssigneesInclude:
        case AutomationConditionType.JobLocation: {
          const labels = condition.data[0].selectedOptions?.map(option => option.label) || [];
          return (
            this.automationDictionaryService.conditionDescriptionsMap[condition.type]?.(labels) ||
            ''
          );
        }

        default:
          return (
            this.automationDictionaryService.conditionDescriptionsMap[condition.type]?.() || ''
          );
      }
    });

    return `${onlyIf} ${conditionsTexts.join(` ${andIf} `)}`;
  }

  public getActionDescription(action: AutomationAction): string {
    switch (action.type) {
      case AutomationActionType.CandidateSendEmail:
        return this.automationDictionaryService.actionDescriptionsMap[
          AutomationActionType.CandidateSendEmail
        ]();
      case AutomationActionType.CandidateAddNote:
      case AutomationActionType.JobAddNote:
        return this.automationDictionaryService.actionDescriptionsMap[
          AutomationActionType.CandidateAddNote
        ]();
      case AutomationActionType.CandidateAddTask:
        return this.automationDictionaryService.actionDescriptionsMap[
          AutomationActionType.CandidateAddTask
        ]();
      case AutomationActionType.CandidateAddTags:
      case AutomationActionType.JobAddTags:
        return this.automationDictionaryService.actionDescriptionsMap[action.type](
          action.data.tags,
        );
      case AutomationActionType.CandidateAddSourceTags:
        return this.automationDictionaryService.actionDescriptionsMap[
          AutomationActionType.CandidateAddSourceTags
        ](action.data.sourceTags);
      case AutomationActionType.CandidateEvaluationRequest:
        return this.automationDictionaryService.actionDescriptionsMap[
          AutomationActionType.CandidateEvaluationRequest
        ](action.data.adminIds.length);
      case AutomationActionType.CandidateCancelEvaluationRequests:
        return this.automationDictionaryService.actionDescriptionsMap[
          AutomationActionType.CandidateCancelEvaluationRequests
        ]();
      case AutomationActionType.CandidateMoveToTheNextStage:
        return this.automationDictionaryService.actionDescriptionsMap[
          AutomationActionType.CandidateMoveToTheNextStage
        ]();
      case AutomationActionType.CandidateAddFollowers: {
        const hasRecruiter = action.data.addRecruiter;
        const hasHiringManager = action.data.addHiringManager;
        const adminsIds = action.data.followerIds;
        const userNames = this.usersDataService
          .getUsersByIds(adminsIds)()
          .filter(user => !!user)
          .map(user => `${user.firstName} ${user.lastName}`);
        const names = [
          ...(hasRecruiter ? [this.translations.recruiter] : []),
          ...(hasHiringManager ? [this.translations.hiringManager] : []),
          ...userNames,
        ];
        return this.automationDictionaryService.actionDescriptionsMap[
          AutomationActionType.CandidateAddFollowers
        ](names);
      }
      case AutomationActionType.CandidateAssignTo: {
        const { offerIds, talentPoolIds } = action.data;

        const jobNames =
          offerIds?.map(offerId => {
            const job = this.briefJobs()[offerId];
            return job ? job.title : '';
          }) || [];
        const talentPoolNames =
          talentPoolIds?.map(talentPoolId => {
            const talentPool = this.talentPools()[talentPoolId];
            return talentPool ? talentPool.title : '';
          }) || [];
        return this.automationDictionaryService.actionDescriptionsMap[
          AutomationActionType.CandidateAssignTo
        ]({ jobs: jobNames, talentPools: talentPoolNames });
      }
      case AutomationActionType.CandidateUnassignFromJob: {
        const { offerIds, talentPoolIds, includeSelf } = action.data;

        const jobNames =
          offerIds?.map(offerId => {
            const job = this.briefJobs()[offerId];
            return job ? job.title : '';
          }) || [];
        const talentPoolNames =
          talentPoolIds?.map(talentPoolId => {
            const talentPool = this.talentPools()[talentPoolId];
            return talentPool ? talentPool.title : '';
          }) || [];

        return this.automationDictionaryService.actionDescriptionsMap[
          AutomationActionType.CandidateUnassignFromJob
        ]({ jobs: jobNames, talentPools: talentPoolNames, includeSelf });
      }
      case AutomationActionType.CandidateCancelScheduledEvents:
        return this.automationDictionaryService.actionDescriptionsMap[
          AutomationActionType.CandidateCancelScheduledEvents
        ]();
      case AutomationActionType.JobAddTeamMembers: {
        const adminsIds = action.data.adminIds;
        const userNames = this.usersDataService
          .getUsersByIds(adminsIds)()
          .filter(user => !!user)
          .map(user => `${user.firstName} ${user.lastName}`);
        return this.automationDictionaryService.actionDescriptionsMap[
          AutomationActionType.JobAddTeamMembers
        ](userNames);
      }
      case AutomationActionType.CandidateShare: {
        const hasRecruiter = action.data.addRecruiter;
        const hasHiringManager = action.data.addHiringManager;
        const adminsIds = action.data.adminIds;
        const userNames = this.usersDataService
          .getUsersByIds(adminsIds)()
          .filter(user => !!user)
          .map(user => `${user.firstName} ${user.lastName}`);
        const names = [
          ...(hasRecruiter ? [this.translations.recruiter] : []),
          ...(hasHiringManager ? [this.translations.hiringManager] : []),
          ...userNames,
        ];
        return this.automationDictionaryService.actionDescriptionsMap[
          AutomationActionType.CandidateShare
        ](names);
      }
      case AutomationActionType.CandidateUnshare: {
        const adminsIds = action.data.adminIds;
        const userNames = this.usersDataService
          .getUsersByIds(adminsIds)()
          .filter(user => !!user)
          .map(user => `${user.firstName} ${user.lastName}`);
        return this.automationDictionaryService.actionDescriptionsMap[
          AutomationActionType.CandidateUnshare
        ](userNames);
      }
      case AutomationActionType.CandidateDisqualify: {
        const reasonId = action.data.disqualifyReasonId;
        const reason = this.disqualifyReasons().find(r => r.id === reasonId)?.name || null;
        return this.automationDictionaryService.actionDescriptionsMap[
          AutomationActionType.CandidateDisqualify
        ](reason);
      }
      case AutomationActionType.JobChangeStatus: {
        const status = action.data.status;
        const statusTranslation = status
          ? this.i18n.translate(jobStatusTranslationKeys[status])
          : null;
        return this.automationDictionaryService.actionDescriptionsMap[
          AutomationActionType.JobChangeStatus
        ](statusTranslation);
      }
      case AutomationActionType.CandidateSendToTellentHR:
        return this.automationDictionaryService.actionDescriptionsMap[
          AutomationActionType.CandidateSendToTellentHR
        ]();
      case AutomationActionType.JobSubmitToJobBoards:
        const jobBoards = action.data.jobBoards;
        return this.automationDictionaryService.actionDescriptionsMap[
          AutomationActionType.JobSubmitToJobBoards
        ](jobBoards.map(jobBoard => jobBoard.name));
      default:
        return '';
    }
  }

  public getAutomationDescription(
    triggerDescription: string,
    conditionsDescription: string,
    actionDescriptions: string[],
  ): string {
    const { triggerPart, conditionsPart, actionsPart } = this.getAutomationDescriptionParts(
      triggerDescription,
      conditionsDescription,
      actionDescriptions,
    );
    return triggerPart + conditionsPart + actionsPart;
  }

  public getAutomationDescriptionParts(
    triggerDescription: string,
    conditionsDescription: string,
    actionDescriptions: string[],
  ): { triggerPart: string; conditionsPart: string; actionsPart: string } {
    const triggerPart = triggerDescription ? `${triggerDescription}` : '';
    const conditionsPart = conditionsDescription ? `&nbsp;${conditionsDescription}` : '';
    let actionsPart = '';
    if (!actionDescriptions.length) {
      actionsPart = '';
    } else if (actionDescriptions.length === 1) {
      actionsPart = `,&nbsp;${this.i18n.translate('app.workflow_automations.then')} ${actionDescriptions[0]}`;
    } else {
      const lastAction = actionDescriptions.pop();
      actionsPart = `,&nbsp;${this.i18n.translate('app.workflow_automations.then')} ${actionDescriptions.join(', ')} ${this.i18n.translate('app.common.and')} ${lastAction}`;
    }
    return { triggerPart, conditionsPart, actionsPart };
  }
}
